import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PenNib, CheckCircle, Trash, ShieldCheck } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SignaturePad from "@/components/SignaturePad";

/**
 * "Mi firma digital" — permite a cada usuario capturar su firma manuscrita
 * una sola vez para reutilizarla como firma auditable en asistencia, PDFs
 * y demás documentos firmables.
 *
 * Al guardarse persiste el data URL en `users.signature_data_url`, junto con
 * `signature_saved_at` y `signature_hash` (SHA-256) para trazabilidad.
 */
export default function SignaturePicker() {
  const [meta, setMeta] = useState(null);   // {signature_data_url, signature_saved_at, signature_hash}
  const [pending, setPending] = useState("");  // draft dataURL from SignaturePad
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMeta({
        signature_data_url: data.signature_data_url || "",
        signature_saved_at: data.signature_saved_at || "",
        signature_hash: data.signature_hash || "",
      });
    } catch (e) { setMeta({ signature_data_url: "", signature_saved_at: "", signature_hash: "" }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!pending) { toast.error("Dibuja tu firma primero"); return; }
    setBusy(true);
    try {
      await api.put("/auth/me/signature", { signature_data_url: pending });
      toast.success("Firma digital guardada");
      setPending("");
      setEditing(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar tu firma digital? Ya no aparecerá en documentos futuros hasta que dibujes una nueva.")) return;
    setBusy(true);
    try {
      await api.delete("/auth/me/signature");
      toast.success("Firma eliminada");
      setEditing(false);
      setPending("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!meta) return null;
  const hasSig = Boolean(meta.signature_data_url);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="signature-picker">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
          <PenNib size={20} weight="fill" className="text-blue-900" />
        </div>
        <div className="flex-1">
          <p className="font-heading font-black text-slate-900">Mi firma digital</p>
          <p className="text-xs text-slate-500">
            Tu firma manuscrita se guarda una sola vez y se estampa automáticamente en tu asistencia diaria + en los PDFs
            donde aparezca tu nombre. Cada firma queda con un hash SHA-256 para auditoría.
          </p>
        </div>
        {hasSig && !editing && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
            <ShieldCheck size={11} weight="bold" /> Activa
          </span>
        )}
      </div>

      {hasSig && !editing ? (
        <div className="border border-slate-200 rounded p-3 bg-slate-50">
          <img
            src={meta.signature_data_url}
            alt="firma"
            className="h-24 mx-auto bg-white rounded border border-slate-200"
            data-testid="signature-picker-preview"
          />
          <div className="mt-2 flex items-center justify-between flex-wrap gap-2 text-[10px] text-slate-500">
            <span>Guardada: <b className="font-mono">{meta.signature_saved_at?.slice(0, 16).replace("T", " ")}</b></span>
            <span title={meta.signature_hash}>Hash: <b className="font-mono">{meta.signature_hash?.slice(0, 12)}…</b></span>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="signature-picker-edit"
                    className="gap-1"><PenNib size={12} weight="bold" /> Redibujar</Button>
            <Button size="sm" variant="outline" onClick={remove} disabled={busy}
                    data-testid="signature-picker-delete"
                    className="gap-1 border-red-200 text-red-700 hover:bg-red-50">
              <Trash size={12} weight="bold" /> Eliminar
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <SignaturePad value={pending} onChange={setPending} width={480} height={140} testid="signature-picker-pad" />
          <div className="mt-2 flex items-center justify-end gap-2">
            {editing && (
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setPending(""); }}
                      className="border-slate-200">
                Cancelar
              </Button>
            )}
            <Button size="sm" onClick={save} disabled={busy || !pending}
                    data-testid="signature-picker-save"
                    className="bg-blue-900 hover:bg-blue-800 gap-1">
              <CheckCircle size={14} weight="bold" /> {busy ? "Guardando…" : "Guardar firma"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
